// ─────────────────────────────────────────────────────────────────
// Glass-i — Glass Category Master page (Stage 6B.8A-3N).
//
// COMPACT INLINE CRUD — no drawer, no modal. Every cell edits live;
// dirty rows highlight in amber; Save All Changes commits via the
// /bulk-save endpoint in one round-trip.
//
// Drives the "Category" dropdown shown in the Glass Item-create
// popup. Each row carries:
//   • categoryName  — what the operator types & what the dropdown
//                     shows (TINTED, ANNEALED, LACQURE, TOUGHENED…)
//   • glassFamily   — enum value written into item_glass_specs.
//   • glassColor    — free-text label preserved on the spec row.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Input, InputNumber, Select, Space, Spin, Table, Tooltip, message
} from 'antd';
import {
  PlusOutlined, SaveOutlined, ReloadOutlined,
  AppstoreOutlined, SearchOutlined
} from '@ant-design/icons';

import { glassCategoryApi } from './glassCategoryApi';
import {
  normalizeGlassCategoryRows, sortGlassCategories, nextSno,
  validateGlassCategoryRows, buildGlassCategoryBulkPayload, markDirtyRow,
  GLASS_FAMILY_OPTIONS
} from './glassCategoryHelpers';
import './GlassCategoryMasterPage.css';

export default function GlassCategoryMasterPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [search,  setSearch]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await glassCategoryApi.list({ limit: 1000 });
      setRows(sortGlassCategories(
        normalizeGlassCategoryRows((out && out.rows) || [])));
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load glass categories.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Filtering ────────────────────────────────────────────────
  const visible = useMemo(() => {
    const needle = (search || '').trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.categoryName, r.glassFamily, r.glassColor, r.remarks, String(r.sno)]
        .some((v) => v && String(v).toLowerCase().includes(needle)));
  }, [rows, search]);

  const dirtyCount = useMemo(
    () => rows.filter((r) => r._dirty).length, [rows]);

  // ── Row mutations (in-memory only — saved on demand) ─────────
  const patch = (clientRowId, p) =>
    setRows((prev) => markDirtyRow(prev, clientRowId, p));

  const addRow = () => {
    setRows((prev) => {
      const fresh = {
        _clientRowId:  `gc-new-${Date.now()}-${prev.length + 1}`,
        _dirty:        true,
        id:            null,
        sno:           nextSno(prev),
        categoryName:  '',
        glassFamily:   'other',
        glassColor:    '',
        isActive:      true,
        remarks:       ''
      };
      return [...prev, fresh];
    });
  };

  // ── Save All ─────────────────────────────────────────────────
  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (dirty.length === 0) {
      message.info('Nothing to save.');
      return;
    }
    const { ok, errors } = validateGlassCategoryRows(rows);
    if (!ok) {
      message.error(errors[0].message);
      return;
    }
    setSaving(true);
    try {
      const payload = buildGlassCategoryBulkPayload(rows, []);
      const out = await glassCategoryApi.bulkSave(payload);
      message.success(
        `Saved ${out.inserted || 0} new · updated ${out.updated || 0}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || 'Failed to save glass categories.');
    } finally {
      setSaving(false);
    }
  };

  // ── Columns (Active + Actions deliberately omitted, matching
  //    the Stage 6B.8A-3M.1 pattern). ─────────────────────────
  const columns = [
    { title: 'S No', dataIndex: 'sno', width: 84, align: 'right',
      render: (v, row) => (
        <InputNumber size="small" min={1} step={1}
          value={v}
          variant="borderless" controls={false}
          style={{ width: '100%', textAlign: 'right' }}
          onChange={(n) => patch(row._clientRowId, {
            sno: n == null ? null : Math.trunc(Number(n)) })} />
      ) },
    { title: 'Category Name', dataIndex: 'categoryName', width: 260,
      render: (v, row) => (
        <Input size="small" variant="borderless"
          value={v} placeholder="e.g., TINTED"
          maxLength={80}
          onChange={(e) => patch(row._clientRowId,
            { categoryName: e.target.value })} />
      ) },
    { title: 'Glass Family', dataIndex: 'glassFamily', width: 180,
      render: (v, row) => (
        <Select size="small" variant="borderless"
          value={v || undefined}
          style={{ width: '100%' }}
          placeholder="Pick family…"
          options={GLASS_FAMILY_OPTIONS.map((o) => ({
            value: o.value, label: o.label }))}
          onChange={(val) => patch(row._clientRowId, { glassFamily: val })} />
      ) },
    { title: 'Glass Color (Label)', dataIndex: 'glassColor', width: 220,
      render: (v, row) => (
        <Input size="small" variant="borderless"
          value={v} placeholder="Preserved on spec row"
          maxLength={80}
          onChange={(e) => patch(row._clientRowId,
            { glassColor: e.target.value })} />
      ) },
    { title: 'Remarks', dataIndex: 'remarks',
      render: (v, row) => (
        <Input size="small" variant="borderless"
          value={v} placeholder="Optional notes"
          maxLength={2000}
          onChange={(e) => patch(row._clientRowId,
            { remarks: e.target.value })} />
      ) }
  ];

  return (
    <div className="gc-shell">
      {/* Header */}
      <div className="gc-headstrip">
        <div className="gc-headstrip__lead">
          <span className="gc-headstrip__icon"><AppstoreOutlined /></span>
          <div className="gc-headstrip__text">
            <span className="gc-headstrip__title">Glass Category Master</span>
            <span className="gc-headstrip__subtitle">
              Edit any cell directly — click Save All Changes to persist.
              Categories drive the Item-create Category dropdown.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="gc-stat-chip"><strong>{rows.length}</strong> total</span>
          {dirtyCount > 0 && (
            <span className="gc-stat-chip gc-stat-chip--dirty">
              <strong>{dirtyCount}</strong> unsaved
            </span>
          )}
        </Space>
      </div>

      {/* Toolbar */}
      <div className="gc-toolbar">
        <Input allowClear size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search category, family, color, remarks…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ width: 320 }} />
        <div className="gc-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small"
          icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" icon={<PlusOutlined />} onClick={addRow}>
          Add Row
        </Button>
        <Button size="small" type="primary" icon={<SaveOutlined />}
          onClick={saveAll} loading={saving}
          disabled={saving || dirtyCount === 0}>
          Save All Changes
        </Button>
      </div>

      {/* Grid */}
      <div className="gc-card">
        <Spin spinning={loading}>
          <Table className="gc-grid" size="small"
            rowKey="_clientRowId"
            dataSource={visible}
            columns={columns}
            pagination={false}
            sticky
            scroll={{ x: 'max-content' }}
            rowClassName={(r) => r._dirty ? 'gc-dirty-row' : ''}
            locale={{
              emptyText: rows.length === 0
                ? 'No glass categories yet. Click Add Row to start.'
                : 'No glass categories match the current search.'
            }} />
        </Spin>
      </div>
    </div>
  );
}
